import DrawerLayout from "@/components/DrawerLayout";
import {
  IGQLInvitationStatus,
  IGQLJrnyUserProfileSortField,
  IGQLSortDirection,
} from "@/graphql-types";
import { useAddCourseRolesToUsersMutation } from "@/graphql/hooks/participations-hook";
import { IconAdd, IconContextMenu, IconUpload } from "@/icons";
import { Unpacked } from "@/types";
import { generateDebug } from "@/utils";
import {
  IGQLUserProfilesQuery,
  useResendInvitationForUserMutation,
  useRevokeInvitationForUserMutation,
  useUserProfilesForCourseQuery,
  useUserProfilesForOrganizationQuery,
  useUserProfilesQuery,
} from "@graphql/users-hook";
import {
  Button,
  Container,
  IconButton,
  Menu,
  MenuItem,
  Stack
} from "@mui/material";
import { DataGrid, GridColDef, GridSortModel } from "@mui/x-data-grid";
import { enqueueSnackbar } from "notistack";
import { useCallback, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { BatchAddUsersDialog } from "./components/BatchAddUsersDialog";
import { RolesCell } from "./components/RolesCell";
import { StatusChip } from "./components/StatusChip";
import { UserCell } from "./components/UserCell";
import UserProfileFilters from "./components/filters/UserProfileFilters";
import { useUserProfileFilters } from "./hooks/useUserProfileFilters";
import { UserProfileFilter } from "./types";
const debug = generateDebug("UsersWithDetailView");
interface UsersWithDetailViewProps {
  routeId: string;
  organizationId?: string;
  courseId?: string;
}
type ListUserProfile = Unpacked<
  IGQLUserProfilesQuery["jrnyUserProfiles"]["items"]
>;
type ContextMenuProps = {
  anchorEl: HTMLElement | null;
  onClose: () => void;
  item: ListUserProfile | null;
  revokeInvitation: (item: ListUserProfile) => void;
  resendInvitation: (item: ListUserProfile) => void;
};
const ContextMenu = ({
  anchorEl,
  onClose,
  item,
  revokeInvitation,
  resendInvitation,
}: ContextMenuProps) => {
  const navigate = useNavigate();
  const open = Boolean(anchorEl && item);

  return (
    <Menu
      anchorEl={anchorEl}
      open={open}
      onClose={onClose}
      MenuListProps={{
        "aria-labelledby": "basic-button",
      }}
    >
      <MenuItem
        onClick={() => {
          onClose();
          if (item) {
            navigate(item.user.id);
          }
        }}
      >
        Profile
      </MenuItem>
      {(item?.status === IGQLInvitationStatus.Pending ||
        item?.status === IGQLInvitationStatus.Revoked) && (
        <>
          <MenuItem
            onClick={() => {
              onClose();
              if (item) {
                resendInvitation(item);
              }
            }}
          >
            Resend Invitation
          </MenuItem>
          <MenuItem
            onClick={() => {
              onClose();
              if (item) {
                revokeInvitation(item);
              }
            }}
          >
            Revoke Invitation
          </MenuItem>
        </>
      )}
    </Menu>
  );
};
const columnToSort = (col: string): IGQLJrnyUserProfileSortField => {
  switch (col) {
    case "createdAt":
      return IGQLJrnyUserProfileSortField.CreatedAt;
    case "status":
      return IGQLJrnyUserProfileSortField.Status;
    case "lastLogin":
      return IGQLJrnyUserProfileSortField.LastLogin;
    case "user":
      return IGQLJrnyUserProfileSortField.Name;
    default:
      return IGQLJrnyUserProfileSortField.CreatedAt;
  }
};
export function UsersWithDetailView({ routeId }: UsersWithDetailViewProps) {
  const { organizationId, courseId } = useParams();
  const navigate = useNavigate();
  const [showBatchAddDialog, setShowBatchAddDialog] = useState(false);

  const [paginationModel, setPaginationModel] = useState({
    pageSize: 10,
    page: 0,
  });
  const [sortModel, setSortModel] = useState<GridSortModel>([
    { field: "createdAt", sort: "desc" },
  ]);
  const [, resendInvitation] = useResendInvitationForUserMutation();
  const [, revokeInvitation] = useRevokeInvitationForUserMutation();
  const [, addMultipleCourseUserMutation] = useAddCourseRolesToUsersMutation();
  const addMultipleCourseUser = useCallback(
    async (users: { email: string; roleIds: string[]; firstName?: string; lastName?: string; message?: string }[]) => {
      debug('addMultipleCourseUser', users);
      if (!courseId) throw new Error('Course ID is required');
      if(users.length === 0) {
        enqueueSnackbar('No users to add', { variant: 'error' });
        return;
      }
      const result = await addMultipleCourseUserMutation({
        input: {
          courseId,
          entries: users.map(user => ({
            emailCandidate: {
              email: user.email,
              firstName: user.firstName || null,
              lastName: user.lastName || null,
            },
            roleIds: user.roleIds,
            message: user.message || null,
          }))
        }
      },{});
      debug('addMultipleCourseUser result', result);
      refetchCourse();
      return result;
    },
    [addMultipleCourseUserMutation, courseId]
  );
  // Use the custom hook for filter management
  const { filters, setFilters, mapFiltersToBackend } = useUserProfileFilters();

  // Define common query variables
  const commonQueryVariables = {
    offset: paginationModel.page * paginationModel.pageSize,
    limit: paginationModel.pageSize,
    sort: sortModel[0]
      ? {
          field: columnToSort(sortModel[0].field),
          direction:
            sortModel[0].sort === "asc"
              ? IGQLSortDirection.Asc
              : IGQLSortDirection.Desc,
        }
      : undefined,
    filter:
      Object.keys(filters).length > 0
        ? mapFiltersToBackend(filters)
        : undefined,
  };

  // Query for organization-specific users
  const [organizationResult] = useUserProfilesForOrganizationQuery({
    variables: {
      ...commonQueryVariables,
      organizationId: organizationId!,
    },
    requestPolicy: "cache-and-network",
    pause: !organizationId || !!courseId, // Pause if no organizationId or if courseId is present
  });

  // Query for course-specific users
  const [courseResult, refetchCourse] = useUserProfilesForCourseQuery({
    variables: {
      ...commonQueryVariables,
      courseId: courseId!,
    },
    requestPolicy: "cache-and-network",
    pause: !courseId, // Pause if no courseId
  });

  // Query for all users (no organization or course filter)
  const [globalResult] = useUserProfilesQuery({
    variables: commonQueryVariables,
    requestPolicy: "cache-and-network",
    pause: !!organizationId || !!courseId, // Pause if organizationId or courseId is present
  });

  // Determine which result to use
  const result = courseId
    ? courseResult
    : organizationId
      ? organizationResult
      : globalResult;

  const { fetching: loading } = result;
  const [items, total] = useMemo(() => {
    if (courseId && courseResult.data) {
      return [
        courseResult.data?.jrnyUserProfilesForCourse.items,
        courseResult.data?.jrnyUserProfilesForCourse.total,
      ];
    } else if (organizationId && organizationResult.data) {
      return [
        organizationResult.data?.jrnyUserProfilesForOrganization.items,
        organizationResult.data?.jrnyUserProfilesForOrganization.total,
      ];
    } else if (globalResult.data) {
      return [
        globalResult.data?.jrnyUserProfiles.items,
        globalResult.data?.jrnyUserProfiles.total,
      ];
    }
    return [[], 0];
  }, [courseResult, organizationResult, globalResult]);
  const columns: GridColDef[] = [
    {
      field: "user",
      headerName: "User",
      flex: 1,
      maxWidth: 350,
      renderCell: (params) => (
        <UserCell
          name={`${params.row.user.firstName} ${params.row.user.lastName}`.trim()}
          email={params.row.user.email}
        />
      ),
    },
    {
      field: "roleSummary",
      headerName: "Roles",
      sortable: false,
      flex: 1,
      renderCell: (params) => (
        // <DebugView>{JSON.stringify(params.row.roleSummary)}</DebugView>
        <RolesCell roleSummary={params.row.roleSummary} />
      ),
    },
    {
      field: "createdAt",
      headerName: "Added",
      width: 120,
      valueFormatter: (params) => new Date(params.value).toLocaleDateString(),
    },
    {
      field: "lastLogin",
      headerName: "Last Login",
      width: 120,
      valueFormatter: (params) =>
        params.value ? new Date(params.value).toLocaleDateString() : "-",
    },
    {
      field: "status",
      headerName: "Status",
      width: 110,
      renderCell: (params) => <StatusChip status={params.row.status} />,
    },
    {
      field: "actions",
      headerName: "",
      width: 32,
      renderCell: (params) => (
        <IconButton
          onClick={(event) => {
            event.stopPropagation();
            setAnchorEl(event.currentTarget);
            setItem(params.row);
          }}
        >
          <IconContextMenu />
        </IconButton>
      ),
    },
  ];

  const handleRowClick = (params: any) => {
    navigate(params.row.user.id);
  };

  const handleSortModelChange = (newModel: GridSortModel) => {
    debug(newModel);
    setSortModel(newModel);
  };
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [item, setItem] = useState<ListUserProfile | null>(null);

  const handleFilterChange = (newFilters: UserProfileFilter) => {
    setFilters(newFilters);
    setPaginationModel({
      ...paginationModel,
      page: 0, // Reset to first page when filters change
    });
  };

  return (
    <DrawerLayout routeId={routeId}>
      <Container>
        <Stack sx={{ width: "100%", gap: 2 }}>
          <Stack sx={{ width: "100%" }} spacing={2}>
            <UserProfileFilters
              filters={filters}
              onChange={handleFilterChange}
              organizationId={organizationId}
              courseId={courseId}
            />
            <Stack
              direction="row"
              spacing={2}
              alignItems="center"
              justifyContent="flex-end"
            >
              {courseId && (
                <Button
                  variant="contained"
                  color="secondary"
                  size="small"
                  onClick={() => setShowBatchAddDialog(true)}
                  startIcon={<IconUpload />}
                >
                  Add Multiple Users
                </Button>
              )}
              <Button
                variant="contained"
                color="secondary"
                size="small"
                onClick={() => navigate("create")}
                startIcon={<IconAdd />}
              >
                Add new user
              </Button>
            </Stack>
            <DataGrid
              rows={items}
              rowCount={total}
              columns={columns}
              onRowClick={handleRowClick}
              getRowId={(row) => row.user.id}
              paginationMode="server"
              sortingMode="server"
              paginationModel={paginationModel}
              onPaginationModelChange={setPaginationModel}
              sortModel={sortModel}
              onSortModelChange={handleSortModelChange}
              loading={loading}
              disableColumnMenu
              disableColumnFilter
              disableColumnSelector
              disableDensitySelector
              hideFooterSelectedRowCount
              rowHeight={64}
            />
          </Stack>
        </Stack>
      </Container>
      <ContextMenu
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        item={item}
        revokeInvitation={async (item) => {
          revokeInvitation({ userId: item.user.id });
        }}
        resendInvitation={(item) => {
          resendInvitation({ userId: item.user.id });
        }}
      />
      {courseId && (
        <BatchAddUsersDialog
          open={showBatchAddDialog}
          onClose={() => setShowBatchAddDialog(false)}
          addUsers={addMultipleCourseUser}
        />
      )}
    </DrawerLayout>
  );
}
